import {createSlice} from '@reduxjs/toolkit';
import {
  FilteredPayloadAction,
  LocationState,
  reducerUtils,
  utils,
} from '@wallaby-ui/core';
import {omit} from 'lodash';

import {
  GRID_ACTION_FILTER,
  GRID_ACTION_PAGE,
  GRID_ACTION_SCROLL,
  GRID_ACTION_SORT,
} from 'src/js/containers/grid/grid_constants';
import {REPORTS_REDUCER_STATE_ID} from 'src/js/containers/reports/reports_constants';

const STATIC_EMPTY_ARRAY = [];

export interface UpdateGridPerformedActionPayload {
  performedAction: Record<string, any>;
  gridId: string;
  detailId: string;
  remove: boolean;
  reportId: string;
  reset: boolean;
}

export interface SetReportsConfigPayload {
  config: Record<string, any>;
  reload: boolean;
}

export interface ChangeReportParamsPayload {
  gridId: string;
  params: any;
}

export interface ReportsResult {
  performedActions: Record<string, any>;
  timestamp?: number;
}

export interface ReportsState {
  reportsResult: ReportsResult;
  locationState: LocationState;
  reportsConfig: Record<string, any>;
}

const initialState: ReportsState = {
  reportsResult: {
    performedActions: {},
    timestamp: undefined,
  },
  locationState: {
    locationParsed: false,
    reload: false,
  },
  reportsConfig: {},
};

export function addGridPerformedAction(
  statePerformedActions: Record<string, any> = {},
  addedPerformedAction: Record<string, any> = {},
  gridId: string | undefined = undefined
) {
  return {
    ...statePerformedActions,
    [gridId]: [
      ...(statePerformedActions[gridId] || STATIC_EMPTY_ARRAY),
      addedPerformedAction,
    ],
  };
}

export function removeGridPerformedAction(
  statePerformedActions: Record<string, any> = {},
  removedPerformedAction: Record<string, any> = {},
  gridId: string | undefined = undefined
) {
  return {
    performedActions: {
      ...statePerformedActions,
      [gridId]: utils.stateUtils.removeItemFromArray(
        statePerformedActions[gridId] || STATIC_EMPTY_ARRAY,
        (item) => item.type === removedPerformedAction.type
      ),
    },
  };
}

export function updateGridPerformedAction(
  statePerformedActions: Record<string, any> = {},
  updatedPerformedAction: Record<string, any> = {},
  gridId: string | undefined = undefined,
  remove: boolean = true,
  reset: boolean = false
) {
  return {
    performedActions: reset
      ? omit(statePerformedActions, [[gridId]])
      : {
          ...statePerformedActions,
          [gridId]: remove
            ? utils.stateUtils.removeItemFromArray(
                statePerformedActions[gridId] || STATIC_EMPTY_ARRAY,
                (item) => item.type === updatedPerformedAction.type
              )
            : [
                ...(statePerformedActions[gridId] || STATIC_EMPTY_ARRAY),
                updatedPerformedAction,
              ],
        },
  };
}

export function modifyGridPerformedActionByParams(
  statePerformedActions: Record<string, any> = {},
  params: Record<string, any> = {},
  gridId: string | undefined = undefined
) {
  let modifiedStatePerformedActions = statePerformedActions;
  if (params.page) {
    modifiedStatePerformedActions = {
      ...addGridPerformedAction(
        modifiedStatePerformedActions,
        {type: GRID_ACTION_PAGE},
        gridId
      ),
    };
  }
  if (params.pagination) {
    modifiedStatePerformedActions = {
      ...addGridPerformedAction(
        modifiedStatePerformedActions,
        {type: GRID_ACTION_PAGE},
        gridId
      ),
    };
  }
  if (params.scroll) {
    modifiedStatePerformedActions = {
      ...addGridPerformedAction(
        modifiedStatePerformedActions,
        {type: GRID_ACTION_SCROLL},
        gridId
      ),
    };
  }
  if (params.sort) {
    modifiedStatePerformedActions = {
      ...addGridPerformedAction(
        modifiedStatePerformedActions,
        {type: GRID_ACTION_SORT},
        gridId
      ),
    };
  }
  if (params.filters) {
    modifiedStatePerformedActions = {
      ...addGridPerformedAction(
        modifiedStatePerformedActions,
        {type: GRID_ACTION_FILTER},
        gridId
      ),
    };
  }
  return modifiedStatePerformedActions;
}

export const reportsSlice = createSlice({
  name: REPORTS_REDUCER_STATE_ID,
  initialState,
  reducers: {
    locationParsed: {
      reducer(state, action: FilteredPayloadAction<boolean>) {
        if (state.locationState.locationParsed !== action.payload) {
          state.locationState.locationParsed = action.payload;
        }
      },
      prepare: reducerUtils.prepareWithStateIdFactory<boolean>(),
    },
    resetReload: {
      reducer(state) {
        if (state.locationState.reload !== false) {
          state.locationState.reload = false;
        }
      },
      prepare: reducerUtils.prepareWithStateIdFactory<void>(),
    },
    setReportsConfig: {
      reducer(state, action: FilteredPayloadAction<SetReportsConfigPayload>) {
        const {config, reload} = action.payload;
        if (reload || config) {
          // return {...state, reportsConfig: config};
          state.reportsConfig = config;
        }
      },
      prepare:
        reducerUtils.prepareWithStateIdFactory<SetReportsConfigPayload>(),
    },
    changeReportParams: {
      reducer(state, action: FilteredPayloadAction<ChangeReportParamsPayload>) {
        const {params, gridId} = action.payload;

        return {
          ...state,
          reportsResult: {
            ...state.reportsResult,
            performedActions: modifyGridPerformedActionByParams(
              state.reportsResult.performedActions,
              params,
              gridId
            ),
            gridParams: {
              ...state.reportsResult.gridParams,
              [gridId]: {
                ...(state.reportsResult.gridParams
                  ? state.reportsResult.gridParams[gridId]
                  : {}),
                ...(params.scroll ? {scroll: [...params.scroll]} : {}),
                ...(params.sort ? {sort: [...params.sort]} : {}),
                ...(params.filters ? {filters: [...params.filters]} : {}),
                ...(params.filteredRowsCount
                  ? {filteredRowsCount: params.filteredRowsCount}
                  : {}),
                pagination: {
                  ...(state.reportsResult.gridParams &&
                    state.reportsResult.gridParams[gridId] &&
                    state.reportsResult.gridParams[gridId].pagination),
                  ...(params.page ? {page: params.page} : {}),
                  ...(params.size ? {pageSize: params.size} : {}),
                  ...params.pagination,
                },
              },
            },
          },
        };
      },
      prepare:
        reducerUtils.prepareWithStateIdFactory<ChangeReportParamsPayload>(),
    },
  },
});
